/**
 * Fraud Category Configuration
 *
 * Maps known Medicaid fraud-risk program categories to specific billing codes.
 * Used by the Price Investigator / Fraud Watchdog feature to surface hospitals
 * with pricing outliers in these categories.
 *
 * Phase 1: Minnesota (14 programs under investigation, $18B billed since 2018)
 * Expandable to other states.
 */

export type CodeType = 'CPT' | 'HCPCS' | 'DRG' | 'REV';

export interface FraudCode {
  code: string;
  codeType: CodeType;
  description: string;
}

export interface FraudCategory {
  id: string;
  name: string;
  shortName: string;
  description: string;
  icon: string;       // Lucide icon name
  color: string;
  states: string[];
  codes: FraudCode[];
  riskIndicators: string[];
}

export const FRAUD_CATEGORIES: FraudCategory[] = [
  {
    id: 'eidbi',
    name: 'Early Intensive Developmental and Behavioral Intervention (EIDBI)',
    shortName: 'EIDBI / Autism',
    description: 'ABA therapy and behavioral analysis for autism spectrum disorders. One of the largest fraud categories in MN Medicaid.',
    icon: 'Brain',
    color: '#7C3AED',
    states: ['MN'],
    codes: [
      { code: '97151', codeType: 'CPT', description: 'Behavior identification assessment' },
      { code: '97152', codeType: 'CPT', description: 'Behavior identification-supporting assessment' },
      { code: '97153', codeType: 'CPT', description: 'Adaptive behavior treatment by protocol' },
      { code: '97154', codeType: 'CPT', description: 'Group adaptive behavior treatment by protocol' },
      { code: '97155', codeType: 'CPT', description: 'Adaptive behavior treatment with protocol modification' },
      { code: '97156', codeType: 'CPT', description: 'Family adaptive behavior treatment guidance' },
      { code: '97157', codeType: 'CPT', description: 'Multiple-family group adaptive behavior treatment' },
      { code: '97158', codeType: 'CPT', description: 'Group adaptive behavior treatment with modification' },
      { code: '0362T', codeType: 'CPT', description: 'Behavior identification assessment, each additional 30 min' },
      { code: '0373T', codeType: 'CPT', description: 'Adaptive behavior treatment, each additional 30 min' },
    ],
    riskIndicators: [
      'Billing for more than 40 hours/week of ABA therapy per patient',
      'Unusually high ratio of senior-level to technician services',
      'Charging 3x+ national average for standard assessments',
      'High volume of modifier-25 claims',
    ],
  },
  {
    id: 'mental-health-rehab',
    name: 'Adult Mental Health Rehabilitation',
    shortName: 'Mental Health Rehab',
    description: 'Outpatient mental health rehabilitation services including therapy, crisis intervention, and psychiatric evaluation.',
    icon: 'HeartPulse',
    color: '#2563EB',
    states: ['MN'],
    codes: [
      { code: '90791', codeType: 'CPT', description: 'Psychiatric diagnostic evaluation' },
      { code: '90792', codeType: 'CPT', description: 'Psychiatric diagnostic evaluation with medical services' },
      { code: '90832', codeType: 'CPT', description: 'Psychotherapy, 30 min' },
      { code: '90834', codeType: 'CPT', description: 'Psychotherapy, 45 min' },
      { code: '90837', codeType: 'CPT', description: 'Psychotherapy, 60 min' },
      { code: '90839', codeType: 'CPT', description: 'Psychotherapy for crisis, first 60 min' },
      { code: '90840', codeType: 'CPT', description: 'Psychotherapy for crisis, each additional 30 min' },
      { code: '90846', codeType: 'CPT', description: 'Family psychotherapy without patient' },
      { code: '90847', codeType: 'CPT', description: 'Family psychotherapy with patient' },
      { code: '90853', codeType: 'CPT', description: 'Group psychotherapy' },
      { code: 'H0001', codeType: 'HCPCS', description: 'Alcohol and/or drug assessment' },
      { code: 'H0004', codeType: 'HCPCS', description: 'Behavioral health counseling and therapy, per 15 min' },
      { code: 'H0031', codeType: 'HCPCS', description: 'Mental health assessment by non-physician' },
      { code: 'H0036', codeType: 'HCPCS', description: 'Community psychiatric supportive treatment, per 15 min' },
      { code: '885', codeType: 'DRG', description: 'Psychoses' },
      { code: '886', codeType: 'DRG', description: 'Behavioral & developmental disorders' },
      { code: '887', codeType: 'DRG', description: 'Other mental disorder diagnoses' },
      { code: '0911', codeType: 'REV', description: 'Psychiatric/psychological services — general' },
    ],
    riskIndicators: [
      'Billing crisis therapy codes for routine sessions',
      'Excessive 60-min sessions (90837) vs 30/45-min alternatives',
      'Phantom patients or duplicate billing across facilities',
      'Unlicensed staff providing services billed at licensed rates',
    ],
  },
  {
    id: 'peer-recovery',
    name: 'Peer Recovery Support Services',
    shortName: 'Peer Recovery',
    description: 'Non-clinical peer support services for mental health and substance use recovery.',
    icon: 'Users',
    color: '#059669',
    states: ['MN'],
    codes: [
      { code: 'H0038', codeType: 'HCPCS', description: 'Self-help/peer services, per 15 min' },
      { code: 'H0004', codeType: 'HCPCS', description: 'Behavioral health counseling and therapy, per 15 min' },
      { code: '90832', codeType: 'CPT', description: 'Psychotherapy, 30 min' },
      { code: '90834', codeType: 'CPT', description: 'Psychotherapy, 45 min' },
      { code: '90853', codeType: 'CPT', description: 'Group psychotherapy' },
      { code: 'H0001', codeType: 'HCPCS', description: 'Alcohol and/or drug assessment' },
    ],
    riskIndicators: [
      'Billing peer support at clinical therapy rates',
      'Excessive hours per patient per week',
      'Peer specialists providing clinical services outside scope',
      'Concurrent billing with other mental health services',
    ],
  },
  {
    id: 'nemt',
    name: 'Non-Emergency Medical Transportation (NEMT)',
    shortName: 'NEMT / Transport',
    description: 'Non-emergency transportation to and from medical appointments. Major fraud vector in MN.',
    icon: 'Ambulance',
    color: '#DC2626',
    states: ['MN'],
    codes: [
      { code: 'A0080', codeType: 'HCPCS', description: 'Non-emergency transportation, per mile — vehicle provided by volunteer' },
      { code: 'A0090', codeType: 'HCPCS', description: 'Non-emergency transportation, per mile — vehicle provided by entity' },
      { code: 'A0100', codeType: 'HCPCS', description: 'Non-emergency transportation, taxi' },
      { code: 'A0110', codeType: 'HCPCS', description: 'Non-emergency transportation, bus, intra/inter city' },
      { code: 'A0120', codeType: 'HCPCS', description: 'Non-emergency transportation, mini-bus' },
      { code: 'A0130', codeType: 'HCPCS', description: 'Non-emergency transportation, wheelchair van' },
      { code: 'A0140', codeType: 'HCPCS', description: 'Non-emergency transportation, air travel' },
      { code: 'A0160', codeType: 'HCPCS', description: 'Non-emergency transportation, per mile — caseworker/social worker' },
      { code: 'A0170', codeType: 'HCPCS', description: 'Transportation ancillary: parking fees, tolls, etc.' },
      { code: 'A0180', codeType: 'HCPCS', description: 'Non-emergency transportation: ancillary, lodging-recipient' },
      { code: 'A0200', codeType: 'HCPCS', description: 'Non-emergency transportation: ancillary, lodging-escort' },
      { code: 'A0210', codeType: 'HCPCS', description: 'Non-emergency transportation: ancillary, meals-recipient' },
    ],
    riskIndicators: [
      'Billing for phantom rides (trips that never occurred)',
      'Inflated mileage claims',
      'Billing for ambulance-level transport for routine visits',
      'Kickback schemes between transport companies and providers',
    ],
  },
  {
    id: 'personal-care',
    name: 'Personal Care Assistance (PCA)',
    shortName: 'Personal Care',
    description: 'In-home assistance with daily living activities like bathing, dressing, and meal preparation.',
    icon: 'HandHelping',
    color: '#0891B2',
    states: ['MN'],
    codes: [
      { code: 'T1019', codeType: 'HCPCS', description: 'Personal care services, per 15 min' },
      { code: 'T1020', codeType: 'HCPCS', description: 'Personal care services, per diem' },
      { code: 'S5135', codeType: 'HCPCS', description: 'Companion care, adult, per 15 min' },
      { code: 'S5136', codeType: 'HCPCS', description: 'Companion care, adult, per diem' },
      { code: 'S9122', codeType: 'HCPCS', description: 'Home health aide or certified nurse assistant, per 15 min' },
    ],
    riskIndicators: [
      'Billing for more than 24 hours of care in a single day',
      'Family members billing as PCAs with inflated hours',
      'Services billed while patient was hospitalized',
      'No documentation of care plan or physician authorization',
    ],
  },
  {
    id: 'adult-day',
    name: 'Adult Day Treatment Services',
    shortName: 'Adult Day Services',
    description: 'Structured day programs providing health, social, and support services for adults with disabilities.',
    icon: 'Sun',
    color: '#D97706',
    states: ['MN'],
    codes: [
      { code: 'S5100', codeType: 'HCPCS', description: 'Day care services, adult, per half day' },
      { code: 'S5101', codeType: 'HCPCS', description: 'Day care services, adult, per full day' },
      { code: 'S5102', codeType: 'HCPCS', description: 'Day care services, adult, per 15 min' },
      { code: '0912', codeType: 'REV', description: 'Psychiatric/psychological services — partial hospitalization, less intensive' },
      { code: '0913', codeType: 'REV', description: 'Psychiatric/psychological services — partial hospitalization, intensive' },
    ],
    riskIndicators: [
      'Billing full-day rates for partial attendance',
      'Ghost patients who never attended',
      'Concurrent billing with other Medicaid services',
      'Inadequate staffing ratios vs billed census',
    ],
  },
  {
    id: 'substance-abuse',
    name: 'Substance Use Disorder Treatment',
    shortName: 'Substance Abuse',
    description: 'Treatment for alcohol and drug use disorders including counseling, detox, and medication-assisted treatment.',
    icon: 'Pill',
    color: '#9333EA',
    states: ['MN'],
    codes: [
      { code: '99406', codeType: 'CPT', description: 'Smoking/tobacco use cessation counseling, 3-10 min' },
      { code: '99407', codeType: 'CPT', description: 'Smoking/tobacco use cessation counseling, >10 min' },
      { code: 'H0001', codeType: 'HCPCS', description: 'Alcohol and/or drug assessment' },
      { code: 'H0004', codeType: 'HCPCS', description: 'Behavioral health counseling and therapy, per 15 min' },
      { code: 'H0005', codeType: 'HCPCS', description: 'Alcohol and/or drug services, group counseling' },
      { code: 'H0006', codeType: 'HCPCS', description: 'Alcohol and/or drug services, case management' },
      { code: 'H0007', codeType: 'HCPCS', description: 'Alcohol and/or drug crisis intervention, per 15 min' },
      { code: 'H0015', codeType: 'HCPCS', description: 'Alcohol and/or drug intensive outpatient treatment' },
      { code: '895', codeType: 'DRG', description: 'Alcohol/drug abuse or dependence w/o rehab therapy w/o MCC' },
      { code: '896', codeType: 'DRG', description: 'Alcohol/drug abuse or dependence w rehab therapy w/o MCC' },
      { code: '897', codeType: 'DRG', description: 'Alcohol/drug abuse or dependence w/o rehab therapy w MCC' },
      { code: '0944', codeType: 'REV', description: 'Alcohol/drug abuse services — other' },
      { code: '0945', codeType: 'REV', description: 'Alcohol/drug abuse services — rehabilitation' },
    ],
    riskIndicators: [
      'Billing individual therapy rates for group sessions',
      'Upcoding routine counseling as crisis intervention',
      'Excessive assessments (H0001) per patient',
      'Billing for services during patient no-shows',
    ],
  },
  {
    id: 'act',
    name: 'Assertive Community Treatment (ACT)',
    shortName: 'ACT Teams',
    description: 'Intensive, team-based community treatment for adults with severe and persistent mental illness.',
    icon: 'UserCheck',
    color: '#0D9488',
    states: ['MN'],
    codes: [
      { code: 'H0039', codeType: 'HCPCS', description: 'Assertive community treatment, face-to-face, per 15 min' },
      { code: 'H0040', codeType: 'HCPCS', description: 'Assertive community treatment, per diem' },
      { code: 'H2015', codeType: 'HCPCS', description: 'Comprehensive community support services, per 15 min' },
      { code: 'H0036', codeType: 'HCPCS', description: 'Community psychiatric supportive treatment, per 15 min' },
    ],
    riskIndicators: [
      'Billing per diem (H0040) without meeting minimum contact requirements',
      'Team composition not meeting ACT model staffing standards',
      'Billing ACT rates for lower-intensity services',
      'Duplicate billing with ARMHS or other community services',
    ],
  },
  {
    id: 'irts',
    name: 'Intensive Residential Treatment Services (IRTS)',
    shortName: 'Residential Treatment',
    description: 'Short-term residential mental health treatment programs as alternative to hospitalization.',
    icon: 'Building',
    color: '#BE185D',
    states: ['MN'],
    codes: [
      { code: '885', codeType: 'DRG', description: 'Psychoses' },
      { code: '886', codeType: 'DRG', description: 'Behavioral & developmental disorders' },
      { code: '887', codeType: 'DRG', description: 'Other mental disorder diagnoses' },
      { code: '0900', codeType: 'REV', description: 'Behavioral health services — general' },
      { code: '0901', codeType: 'REV', description: 'Behavioral health services — psychiatric' },
      { code: '0905', codeType: 'REV', description: 'Behavioral health services — intensive outpatient' },
      { code: '0911', codeType: 'REV', description: 'Psychiatric/psychological services — general' },
      { code: '0914', codeType: 'REV', description: 'Psychiatric/psychological services — individual therapy' },
      { code: '0915', codeType: 'REV', description: 'Psychiatric/psychological services — group therapy' },
      { code: '0916', codeType: 'REV', description: 'Psychiatric/psychological services — family therapy' },
    ],
    riskIndicators: [
      'Extended stays beyond clinically appropriate duration',
      'Minimal therapeutic programming during residential stay',
      'Billing residential rates for patients on home passes',
      'Inadequate discharge planning to justify longer stays',
    ],
  },
  {
    id: 'recuperative-care',
    name: 'Recuperative Care / Rehabilitation',
    shortName: 'Recuperative Care',
    description: 'Post-acute rehabilitation services including physical therapy, occupational therapy, and skilled nursing.',
    icon: 'Activity',
    color: '#EA580C',
    states: ['MN'],
    codes: [
      { code: '97110', codeType: 'CPT', description: 'Therapeutic exercises, each 15 min' },
      { code: '97112', codeType: 'CPT', description: 'Neuromuscular reeducation, each 15 min' },
      { code: '97116', codeType: 'CPT', description: 'Gait training, each 15 min' },
      { code: '97140', codeType: 'CPT', description: 'Manual therapy techniques, each 15 min' },
      { code: '97161', codeType: 'CPT', description: 'PT evaluation, low complexity' },
      { code: '97162', codeType: 'CPT', description: 'PT evaluation, moderate complexity' },
      { code: '97163', codeType: 'CPT', description: 'PT evaluation, high complexity' },
      { code: '97530', codeType: 'CPT', description: 'Therapeutic activities, each 15 min' },
      { code: '97535', codeType: 'CPT', description: 'Self-care/home management training, each 15 min' },
      { code: '97542', codeType: 'CPT', description: 'Wheelchair management training, each 15 min' },
      { code: '945', codeType: 'DRG', description: 'Rehabilitation w CC/MCC' },
      { code: '946', codeType: 'DRG', description: 'Rehabilitation w/o CC/MCC' },
    ],
    riskIndicators: [
      'Billing more therapy units than minutes in the day allow',
      'Upcoding evaluation complexity (97163 vs 97161)',
      'Providing therapy services without individualized care plan',
      'Billing skilled therapy for maintenance-level services',
    ],
  },
  {
    id: 'ics',
    name: 'Integrated Community Supports',
    shortName: 'Community Supports',
    description: 'Wraparound community services integrating housing, health, and social support.',
    icon: 'Network',
    color: '#4F46E5',
    states: ['MN'],
    codes: [
      { code: 'H0043', codeType: 'HCPCS', description: 'Supported housing, per diem' },
      { code: 'H2015', codeType: 'HCPCS', description: 'Comprehensive community support services, per 15 min' },
      { code: 'T2024', codeType: 'HCPCS', description: 'Service plan development/modification, per 15 min' },
      { code: 'H0036', codeType: 'HCPCS', description: 'Community psychiatric supportive treatment, per 15 min' },
    ],
    riskIndicators: [
      'Billing community support for housing-only services',
      'Lack of integrated service delivery across providers',
      'Excessive service plan modifications without clinical justification',
      'Billing for services when client is not present or engaged',
    ],
  },
  {
    id: 'home-supports',
    name: 'Individualized Home Supports',
    shortName: 'Home Supports',
    description: 'In-home services to help individuals with disabilities maintain independence.',
    icon: 'Home',
    color: '#16A34A',
    states: ['MN'],
    codes: [
      { code: 'T2024', codeType: 'HCPCS', description: 'Service plan development/modification, per 15 min' },
      { code: '99341', codeType: 'CPT', description: 'Home visit, new patient, low complexity' },
      { code: '99342', codeType: 'CPT', description: 'Home visit, new patient, moderate complexity' },
      { code: '99344', codeType: 'CPT', description: 'Home visit, new patient, high complexity' },
      { code: '99345', codeType: 'CPT', description: 'Home visit, new patient, comprehensive' },
      { code: '99347', codeType: 'CPT', description: 'Home visit, established patient, low complexity' },
      { code: '99348', codeType: 'CPT', description: 'Home visit, established patient, moderate complexity' },
      { code: '99349', codeType: 'CPT', description: 'Home visit, established patient, high complexity' },
      { code: '99350', codeType: 'CPT', description: 'Home visit, established patient, comprehensive' },
    ],
    riskIndicators: [
      'Billing home visits without actually entering the home',
      'Upcoding visit complexity (99350 vs 99347)',
      'Excessive frequency of home visits without medical necessity',
      'Billing for services already covered by PCA or waiver',
    ],
  },
  {
    id: 'companion',
    name: 'Adult Companion Services',
    shortName: 'Companion Care',
    description: 'Non-medical companion services providing socialization and oversight for adults with disabilities.',
    icon: 'UserPlus',
    color: '#CA8A04',
    states: ['MN'],
    codes: [
      { code: 'S5135', codeType: 'HCPCS', description: 'Companion care, adult, per 15 min' },
      { code: 'S5136', codeType: 'HCPCS', description: 'Companion care, adult, per diem' },
      { code: 'T1019', codeType: 'HCPCS', description: 'Personal care services, per 15 min' },
    ],
    riskIndicators: [
      'Billing companion care as clinical services',
      'Overlapping companion and PCA billing for same hours',
      'Companion services billed during school or work hours',
      'No supervision or oversight of companion providers',
    ],
  },
  {
    id: 'housing-stabilization',
    name: 'Housing Stabilization / Night Supervision',
    shortName: 'Housing / Night',
    description: 'Housing stabilization services and overnight supervision for individuals with mental health or disability needs.',
    icon: 'Moon',
    color: 'var(--civic-navy)',
    states: ['MN'],
    codes: [
      { code: 'T2025', codeType: 'HCPCS', description: 'Waiver services, not otherwise specified' },
      { code: 'T2024', codeType: 'HCPCS', description: 'Service plan development/modification, per 15 min' },
      { code: 'H0043', codeType: 'HCPCS', description: 'Supported housing, per diem' },
      { code: 'H2015', codeType: 'HCPCS', description: 'Comprehensive community support services, per 15 min' },
    ],
    riskIndicators: [
      'Billing night supervision for awake staff when asleep is appropriate',
      'Housing stabilization billed without actual housing support activities',
      'Excessive per-diem charges compared to actual service delivery',
      'Duplicate billing across housing and community support programs',
    ],
  },
];

/**
 * Get category by ID
 */
export function getCategoryById(id: string): FraudCategory | undefined {
  return FRAUD_CATEGORIES.find((c) => c.id === id);
}

/**
 * Get all categories for a state
 */
export function getCategoriesForState(state: string): FraudCategory[] {
  return FRAUD_CATEGORIES.filter((c) => c.states.includes(state));
}

/**
 * Get all unique codes for a category, grouped by code type
 */
export function getCategoryCodesByType(category: FraudCategory): Record<CodeType, string[]> {
  const grouped: Record<CodeType, Set<string>> = {
    CPT: new Set(),
    HCPCS: new Set(),
    DRG: new Set(),
    REV: new Set(),
  };
  for (const code of category.codes) {
    grouped[code.codeType].add(code.code);
  }
  return {
    CPT: [...grouped.CPT],
    HCPCS: [...grouped.HCPCS],
    DRG: [...grouped.DRG],
    REV: [...grouped.REV],
  };
}

/**
 * State context metadata (for the banner)
 */
export interface StateContext {
  state: string;
  stateName: string;
  description: string;
  programCount: number;
  totalBilled: string;
  estimatedFraud: string;
  since: number;
}

export const STATE_CONTEXTS: Record<string, StateContext> = {
  MN: {
    state: 'MN',
    stateName: 'Minnesota',
    description: '14 Medicaid programs under active fraud investigation by the Office of the Legislative Auditor',
    programCount: 14,
    totalBilled: '$18B',
    estimatedFraud: '$9B',
    since: 2018,
  },
};

/**
 * Risk level thresholds
 */
export type RiskLevel = 'low' | 'moderate' | 'high' | 'extreme';

export function getRiskLevel(markupPct: number): RiskLevel {
  if (markupPct > 300) return 'extreme';
  if (markupPct > 100) return 'high';
  if (markupPct > 30) return 'moderate';
  return 'low';
}

export function getRiskColor(level: RiskLevel): string {
  switch (level) {
    case 'low': return '#22c55e';
    case 'moderate': return '#eab308';
    case 'high': return '#f97316';
    case 'extreme': return '#ef4444';
  }
}
